import {
  AibBlockLine,
  AioExternalSingle,
  AitTableData,
  ArsListingBody,
  ArsTableBody,
  FigureData,
  MosaicTrafficLightSwitch,
} from "aic";
import axios, { AxiosResponse } from "axios";
import { AnnotationWhereGroup } from "components/annotations/AnnotationWhereGroup";
import { EditorWithAnnotation } from "components/annotations/interface";
import { v1FigureBody } from "components/tlfpage/V1Figure";
import { IEditorV3 } from "editor-v3";
import { isValidUriString } from "functions/isValidUriString";
import {
  eHierarchy,
  eHierarchyLevel,
  eLinkType,
  eReleaseStatus,
  eTlfItemType,
  eTlfOutputType,
} from "../enums";
import { processAxiosError } from "../processAxiosError";
import { ITlfItemReturn } from "./patchTlfItem";
import { ITlfItemRelationship } from "./TlfItemIndexReadDto";
import { TlfItemReadDto } from "./TlfItemReadDto";
import { tlfPreProcess } from "./tlfPreProcess";

export interface TlfItemRelationshipsDto {
  id: number;
  linkId: string;
  linkType: eLinkType;
  azsolId: string;
  version: number;
}

export interface TlfLookUpDto {
  id: string;
  azsolId: string;
  version: number;
  tlfReleaseStatus?: eReleaseStatus;
}

export interface TlfItemCreateDto {
  azsolId: string;
  additionalInformation: string;
  type: eTlfItemType;
  domain: string;
  crystalReference: string;
  programName: string;
  suffix: string;
  outputName: string;
  outputType: eTlfOutputType;
  sapReference: string;
  standardsNotes: IEditorV3;
  programmerNotes: IEditorV3;
  titles: AibBlockLine<IEditorV3>[];
  pageBy: EditorWithAnnotation<AnnotationWhereGroup>;
  body:
    | AitTableData<IEditorV3>
    | ArsListingBody<IEditorV3>
    | ArsTableBody<IEditorV3>
    | v1FigureBody
    | FigureData;
  footnotes: AibBlockLine<IEditorV3>[];
  singles: AioExternalSingle<IEditorV3>[];
  primaryHierarchyId: string;
  relationships: TlfItemRelationshipsDto[];
  tocOrder?: number;
  tocNumber?: string;
  copiedFromTlfLookup?: TlfLookUpDto;
  releaseDate?: string;
  basedOnTlfLookup?: TlfLookUpDto;
  shellMinorVersion?: number;
  copiedFromHierarchyLevel?: eHierarchyLevel;
}

export interface TlfItemUpdateDto {
  additionalInformation: string;
  type: eTlfItemType;
  domain: string;
  crystalReference: string;
  coreTrafficLightSwitch?: MosaicTrafficLightSwitch;
  programName: string;
  suffix: string;
  outputName: string;
  outputType: eTlfOutputType;
  sapReference: string;
  standardsNotes: IEditorV3;
  programmerNotes: IEditorV3;
  titles: AibBlockLine<IEditorV3>[];
  pageBy: EditorWithAnnotation<AnnotationWhereGroup>;
  body:
    | AitTableData<IEditorV3>
    | ArsListingBody<IEditorV3>
    | ArsTableBody<IEditorV3>
    | v1FigureBody
    | FigureData;
  footnotes: AibBlockLine<IEditorV3>[];
  singles: AioExternalSingle<IEditorV3>[];
  shellMinorVersion?: number;
  tocLabel?: string;
  tocNumber?: string;
}

export interface SaveTlfItemDetails {
  tlfItem: TlfItemReadDto;
  newDetails?: {
    azsolId: string;
    version: number;
    domain: string;
    additionalInformation: string;
    primaryHierarchyId: string;
  };
  copiedFromHierarchyLevel?: eHierarchyLevel;
}

/**
 * Save or update a TLF item depending on provided details.
 *
 * @param backEndUrl - Base backend URL
 * @param saveDetails - Details including the TLF item and optional creation info
 * @param controller - AbortController for cancelling the request
 * @returns Saved or updated TLF item wrapped in ITlfItemReturn
 */
export const saveTlfItem = async (
  backEndUrl: string,
  saveDetails: SaveTlfItemDetails,
  controller: AbortController,
): Promise<ITlfItemReturn> => {
  try {
    let res: AxiosResponse;
    const tlfItem = saveDetails.tlfItem;
    const basedOnTlfLookup: TlfLookUpDto = { id: "", azsolId: "", version: -1 };

    /* istanbul ignore else */
    if (saveDetails.tlfItem.relationships.some((r) => r.linkType === eLinkType.BasedOn)) {
      const basedOnLink = saveDetails.tlfItem.relationships.filter(
        (r) => r.linkType === eLinkType.BasedOn,
      )[0];
      basedOnTlfLookup.azsolId = basedOnLink.azsolId;
      basedOnTlfLookup.version = basedOnLink.version;
      basedOnTlfLookup.id = basedOnLink.linkId;
    }

    // Create
    if (saveDetails.newDetails) {
      const newDetails = saveDetails.newDetails;
      // Get template reference
      const newRels: ITlfItemRelationship[] = [];
      const copiedFromTlfLookup: TlfLookUpDto = { id: "", azsolId: "", version: -1 };

      copiedFromTlfLookup.id = saveDetails.tlfItem.id;
      copiedFromTlfLookup.azsolId = saveDetails.tlfItem.azsolId;
      copiedFromTlfLookup.version = saveDetails.tlfItem.version;

      const newTlfItem: TlfItemCreateDto = {
        azsolId: newDetails.azsolId,
        additionalInformation: newDetails.additionalInformation,
        type: tlfItem.type,
        domain: newDetails.domain,
        crystalReference: tlfItem.crystalReference ?? "",
        programName: tlfItem.programName ?? "",
        suffix: tlfItem.suffix ?? "",
        outputName: tlfItem.outputName ?? "",
        outputType: tlfItem.outputType ?? eTlfOutputType.Rtf,
        sapReference: tlfItem.sapReference,
        standardsNotes: tlfItem.standardsNotes,
        programmerNotes: tlfItem.programmerNotes,
        titles: tlfItem.titles,
        pageBy: tlfItem.pageBy,
        body: tlfItem.body,
        footnotes: tlfItem.footnotes,
        singles: tlfItem.singles,
        primaryHierarchyId: newDetails.primaryHierarchyId,
        relationships: newRels,
        tocOrder: tlfItem.tocOrder,
        tocNumber: tlfItem.tocNumber,
        copiedFromTlfLookup: copiedFromTlfLookup,
        basedOnTlfLookup: basedOnTlfLookup,
        copiedFromHierarchyLevel: saveDetails.copiedFromHierarchyLevel,
      };
      res = await axios.post(`${backEndUrl}TlfItems`, newTlfItem, { signal: controller.signal });
    }
    // Update
    else {
      // Update body depending on type
      const newBody = tlfItem.body;
      /* istanbul ignore else */
      if (tlfItem.type === eTlfItemType.table || tlfItem.type === eTlfItemType.listing) {
        (newBody as AitTableData<IEditorV3>).changedColumns = [];
        (newBody as AitTableData<IEditorV3>).changedRowGroups = [];
      }
      const updateTlfItem: TlfItemUpdateDto = {
        additionalInformation: tlfItem.additionalInformation,
        type: tlfItem.type,
        domain: tlfItem.domain,
        crystalReference: tlfItem.crystalReference ?? "",
        coreTrafficLightSwitch: tlfItem.coreTrafficLightSwitch,
        programName: tlfItem.programName ?? "",
        suffix: tlfItem.suffix ?? "",
        outputName: tlfItem.outputName ?? "",
        outputType: tlfItem.outputType ?? eTlfOutputType.Rtf,
        sapReference: tlfItem.sapReference,
        standardsNotes: tlfItem.standardsNotes,
        programmerNotes: tlfItem.programmerNotes,
        titles: tlfItem.titles,
        pageBy: tlfItem.pageBy,
        body: newBody,
        footnotes: tlfItem.footnotes,
        singles: tlfItem.singles,
        shellMinorVersion:
          tlfItem.primaryHierarchyType === eHierarchy.Report &&
          tlfItem.shellMinorVersion !== undefined
            ? tlfItem.shellMinorVersion + 1
            : tlfItem.shellMinorVersion,
      };
      res = await axios.put(
        `${backEndUrl}TlfItems/${tlfItem.id}${isValidUriString(tlfItem.azsolId) ? `/${tlfItem.azsolId}` : ""}`,
        updateTlfItem,
        { signal: controller.signal },
      );
    }
    return { success: true, data: tlfPreProcess(res.data) };
  } catch (error: unknown) {
    return processAxiosError("Save failed", error);
  }
};
